import { Inflate, strFromU8 } from 'fflate';
import { crc32 } from './crc32';
import { DEFAULT_ZIP_LIMITS, type ZipLimits } from './limits';
import { decodeEntryName, unsafeNameReason } from './names';
import { memorySink, type ByteSink, type ByteSource } from './source';

// 9.18.10.4 · D-830 — a ZIP reader for an archive the person downloaded
// themselves, read on their device. It lists entries from the central
// directory and extracts one entry at a time, a range at a time, checking each
// against what the archive declared. Nothing about Instagram lives here; the
// archive's own layout is read by the importer built on top.
//
// It trusts nothing it has not checked: names that could point outside the
// archive, the same name twice, entries that overlap, sizes that do not add
// up, a compression ratio that only a bomb would have, and a checksum that
// does not match all refuse the archive or the entry.

export type ZipErrorCode =
  | 'not_a_zip'
  | 'unsupported_archive'
  | 'corrupt'
  | 'too_large'
  | 'too_many_entries'
  | 'unsafe_name'
  | 'duplicate_name'
  | 'bomb'
  | 'entry_unavailable'
  | 'checksum'
  | 'cancelled';

export class ZipError extends Error {
  constructor(readonly code: ZipErrorCode, readonly detail?: string) {
    super(detail ? `${code}: ${detail}` : code);
  }
}

export type ZipEntry = {
  name: string;
  directory: boolean;
  encrypted: boolean;
  /** 0 stored, 8 deflated; anything else cannot be read. */
  method: number;
  crc32: number;
  compressedSize: number;
  uncompressedSize: number;
  localHeaderOffset: number;
  /** The archive's DOS timestamp, in the device's time zone; not proof of anything. */
  modifiedAt: Date | null;
  /** Why this entry cannot be extracted, or null when it can. */
  unavailable: null | 'directory' | 'encrypted' | 'method' | 'too_large';
  /** Where this entry's data must end: the next entry's header or the central directory. */
  readonly dataLimit: number;
};

export type ZipArchive = { entries: ZipEntry[] };

const SIG_LOCAL = 0x04034b50;
const SIG_CENTRAL = 0x02014b50;
const SIG_EOCD = 0x06054b50;
const SIG_ZIP64_LOCATOR = 0x07064b50;
const SIG_ZIP64_EOCD = 0x06064b50;
const EOCD_BYTES = 22;
const MAX_COMMENT = 0xffff;
// One deflate input chunk can expand about a thousandfold; 32 KiB keeps a
// lying archive's worst single step near 32 MiB of memory.
const INFLATE_INPUT_BYTES = 32 * 1024;
const STORED_CHUNK_BYTES = 1024 * 1024;

const u16 = (b: Uint8Array, o: number) => b[o] | (b[o + 1] << 8);
const u32 = (b: Uint8Array, o: number) => (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0;
function u64(b: Uint8Array, o: number): number {
  const high = u32(b, o + 4);
  // Beyond 2^53 a JavaScript number stops being exact; no real archive needs it.
  if (high >= 0x200000) throw new ZipError('too_large', '64-bit field');
  return high * 0x100000000 + u32(b, o);
}

function dosDate(date: number, time: number): Date | null {
  const day = date & 0x1f;
  const month = (date >> 5) & 0x0f;
  if (!day || !month || month > 12) return null;
  return new Date(1980 + (date >> 9), month - 1, day, time >> 11, (time >> 5) & 0x3f, (time & 0x1f) * 2);
}

async function readSafely(source: ByteSource, offset: number, length: number): Promise<Uint8Array> {
  if (offset < 0 || length < 0 || offset + length > source.size) throw new ZipError('corrupt', 'range outside the archive');
  const bytes = await source.read(offset, length);
  if (bytes.length !== length) throw new ZipError('corrupt', 'short read');
  return bytes;
}

/** Reads the central directory. Throws a ZipError when the archive cannot be trusted. */
export async function openZip(source: ByteSource, limits: ZipLimits = DEFAULT_ZIP_LIMITS): Promise<ZipArchive> {
  if (source.size < EOCD_BYTES) throw new ZipError('not_a_zip');
  if (source.size > limits.maxArchiveBytes) throw new ZipError('too_large', 'archive');

  const tailLength = Math.min(source.size, EOCD_BYTES + MAX_COMMENT);
  const tailStart = source.size - tailLength;
  const tail = await readSafely(source, tailStart, tailLength);
  let eocd = -1;
  for (let i = tail.length - EOCD_BYTES; i >= 0; i -= 1) {
    // The record must end exactly at the end of the file, comment included.
    if (u32(tail, i) === SIG_EOCD && i + EOCD_BYTES + u16(tail, i + 20) === tail.length) { eocd = i; break; }
  }
  if (eocd < 0) throw new ZipError('not_a_zip');

  const eocdAt = tailStart + eocd;
  let entriesOnDisk = u16(tail, eocd + 8);
  let entryCount = u16(tail, eocd + 10);
  let directorySize = u32(tail, eocd + 12);
  let directoryOffset = u32(tail, eocd + 16);
  let directoryEnd = eocdAt;
  if (entriesOnDisk === 0xffff || entryCount === 0xffff || directorySize === 0xffffffff || directoryOffset === 0xffffffff) {
    if (eocdAt < 20) throw new ZipError('corrupt', 'zip64 locator');
    const locator = await readSafely(source, eocdAt - 20, 20);
    if (u32(locator, 0) !== SIG_ZIP64_LOCATOR) throw new ZipError('corrupt', 'zip64 locator');
    if (u32(locator, 4) !== 0 || u32(locator, 16) > 1) throw new ZipError('unsupported_archive', 'multi-disk');
    const recordAt = u64(locator, 8);
    if (recordAt + 56 > eocdAt - 20) throw new ZipError('corrupt', 'zip64 record');
    const record = await readSafely(source, recordAt, 56);
    if (u32(record, 0) !== SIG_ZIP64_EOCD) throw new ZipError('corrupt', 'zip64 record');
    if (u32(record, 16) !== 0 || u32(record, 20) !== 0) throw new ZipError('unsupported_archive', 'multi-disk');
    entriesOnDisk = u64(record, 24);
    entryCount = u64(record, 32);
    directorySize = u64(record, 40);
    directoryOffset = u64(record, 48);
    directoryEnd = recordAt;
  } else if (u16(tail, eocd + 4) !== 0 || u16(tail, eocd + 6) !== 0) {
    throw new ZipError('unsupported_archive', 'multi-disk');
  }
  if (entriesOnDisk !== entryCount) throw new ZipError('unsupported_archive', 'multi-disk');
  if (entryCount > limits.maxEntries) throw new ZipError('too_many_entries');
  if (directorySize > limits.maxCentralDirectoryBytes) throw new ZipError('too_large', 'central directory');
  // Nothing may hide between the directory and the end records.
  if (directoryOffset + directorySize !== directoryEnd) throw new ZipError('corrupt', 'central directory bounds');

  const directory = await readSafely(source, directoryOffset, directorySize);
  const parsed: Omit<ZipEntry, 'dataLimit'>[] = [];
  const names = new Set<string>();
  const offsets = new Set<number>();
  let total = 0;
  let p = 0;
  for (let index = 0; index < entryCount; index += 1) {
    if (p + 46 > directory.length || u32(directory, p) !== SIG_CENTRAL) throw new ZipError('corrupt', 'central entry');
    const flags = u16(directory, p + 8);
    const method = u16(directory, p + 10);
    const time = u16(directory, p + 12);
    const date = u16(directory, p + 14);
    const checksum = u32(directory, p + 16);
    let compressedSize = u32(directory, p + 20);
    let uncompressedSize = u32(directory, p + 24);
    const nameLength = u16(directory, p + 28);
    const extraLength = u16(directory, p + 30);
    const commentLength = u16(directory, p + 32);
    let diskStart = u16(directory, p + 34);
    let localHeaderOffset = u32(directory, p + 42);
    const end = p + 46 + nameLength + extraLength + commentLength;
    if (end > directory.length) throw new ZipError('corrupt', 'central entry');
    if (nameLength > limits.maxNameBytes) throw new ZipError('unsafe_name', 'too long');

    // ZIP64 sizes and offset, in this order, only for the fields marked as full.
    let e = p + 46 + nameLength;
    const extraEnd = e + extraLength;
    while (e + 4 <= extraEnd) {
      const id = u16(directory, e);
      const size = u16(directory, e + 2);
      if (e + 4 + size > extraEnd) throw new ZipError('corrupt', 'extra field');
      if (id === 0x0001) {
        let f = e + 4;
        const need = (count: number) => { if (f + count > e + 4 + size) throw new ZipError('corrupt', 'zip64 extra'); };
        if (uncompressedSize === 0xffffffff) { need(8); uncompressedSize = u64(directory, f); f += 8; }
        if (compressedSize === 0xffffffff) { need(8); compressedSize = u64(directory, f); f += 8; }
        if (localHeaderOffset === 0xffffffff) { need(8); localHeaderOffset = u64(directory, f); f += 8; }
        if (diskStart === 0xffff) { need(4); diskStart = u32(directory, f); f += 4; }
      }
      e += 4 + size;
    }
    if (diskStart !== 0) throw new ZipError('unsupported_archive', 'multi-disk');

    const name = decodeEntryName(directory.subarray(p + 46, p + 46 + nameLength), (flags & 0x0800) !== 0);
    const unsafe = unsafeNameReason(name);
    if (unsafe) throw new ZipError('unsafe_name', unsafe);
    if (names.has(name)) throw new ZipError('duplicate_name', name);
    names.add(name);
    if (offsets.has(localHeaderOffset)) throw new ZipError('corrupt', 'shared local header');
    offsets.add(localHeaderOffset);
    if (localHeaderOffset + 30 + compressedSize > directoryOffset) throw new ZipError('corrupt', 'entry outside the data area');

    const isDirectory = name.endsWith('/');
    total += uncompressedSize;
    if (total > limits.maxTotalUncompressedBytes) throw new ZipError('too_large', 'total');
    if (!isDirectory && uncompressedSize > limits.ratioFloorBytes
      && (compressedSize === 0 || uncompressedSize / compressedSize > limits.maxCompressionRatio)) {
      throw new ZipError('bomb', name);
    }
    const encrypted = (flags & 0x0001) !== 0;
    parsed.push({
      name,
      directory: isDirectory,
      encrypted,
      method,
      crc32: checksum,
      compressedSize,
      uncompressedSize,
      localHeaderOffset,
      modifiedAt: dosDate(date, time),
      unavailable: isDirectory ? 'directory'
        : encrypted ? 'encrypted'
          : method !== 0 && method !== 8 ? 'method'
            : uncompressedSize > limits.maxEntryBytes ? 'too_large'
              : null,
    });
    p = end;
  }
  if (p !== directory.length) throw new ZipError('corrupt', 'central directory length');

  // Each entry's data must stop before the next entry begins: overlapping
  // entries are how the worst bombs reuse the same bytes many times.
  const starts = [...offsets].sort((a, b) => a - b);
  const next = new Map<number, number>();
  starts.forEach((start, i) => next.set(start, i + 1 < starts.length ? starts[i + 1] : directoryOffset));
  return { entries: parsed.map((entry) => ({ ...entry, dataLimit: next.get(entry.localHeaderOffset)! })) };
}

export type ExtractOptions = {
  /** Checked between chunks; a set flag stops with `cancelled` and discards the output. */
  signal?: { readonly aborted: boolean };
  onProgress?: (written: number, total: number) => void;
  /** Smaller chunks for tests; the defaults bound memory per step. */
  storedChunkBytes?: number;
  inflateInputBytes?: number;
};

const yieldToInterface = () => new Promise<void>((resolve) => setTimeout(resolve, 0));

/** Extracts one entry into `sink`, checking size and checksum. Discards the output on any failure. */
export async function extractEntry<T>(source: ByteSource, entry: ZipEntry, sink: ByteSink<T>, options: ExtractOptions = {}): Promise<T> {
  try {
    if (entry.unavailable) throw new ZipError('entry_unavailable', entry.unavailable);
    const header = await readSafely(source, entry.localHeaderOffset, 30);
    if (u32(header, 0) !== SIG_LOCAL) throw new ZipError('corrupt', 'local header');
    const flags = u16(header, 6);
    if (u16(header, 8) !== entry.method || ((flags & 0x0001) !== 0) !== entry.encrypted) {
      throw new ZipError('corrupt', 'local and central headers disagree');
    }
    const nameLength = u16(header, 26);
    const extraLength = u16(header, 28);
    const localName = await readSafely(source, entry.localHeaderOffset + 30, nameLength);
    if (decodeEntryName(localName, (flags & 0x0800) !== 0) !== entry.name) throw new ZipError('corrupt', 'local name');
    const dataStart = entry.localHeaderOffset + 30 + nameLength + extraLength;
    if (dataStart + entry.compressedSize > entry.dataLimit) throw new ZipError('corrupt', 'entries overlap');

    let written = 0;
    let checksum = 0;
    let steps = 0;
    const emit = async (bytes: Uint8Array) => {
      if (!bytes.length) return;
      written += bytes.length;
      if (written > entry.uncompressedSize) throw new ZipError('bomb', 'more data than declared');
      checksum = crc32(bytes, checksum);
      await sink.write(bytes);
      options.onProgress?.(written, entry.uncompressedSize);
    };
    const between = async () => {
      if (options.signal?.aborted) throw new ZipError('cancelled');
      steps += 1;
      if (steps % 8 === 0) await yieldToInterface();
    };

    if (entry.method === 0) {
      if (entry.compressedSize !== entry.uncompressedSize) throw new ZipError('corrupt', 'stored sizes differ');
      const step = options.storedChunkBytes ?? STORED_CHUNK_BYTES;
      for (let at = 0; at < entry.compressedSize; at += step) {
        await between();
        await emit(await readSafely(source, dataStart + at, Math.min(step, entry.compressedSize - at)));
      }
    } else {
      const step = options.inflateInputBytes ?? INFLATE_INPUT_BYTES;
      const pending: Uint8Array[] = [];
      let ended = false;
      const inflater = new Inflate((data, final) => { pending.push(data); if (final) ended = true; });
      for (let at = 0; at < entry.compressedSize || at === 0; at += step) {
        await between();
        const length = Math.min(step, entry.compressedSize - at);
        const input = length > 0 ? await readSafely(source, dataStart + at, length) : new Uint8Array(0);
        try {
          inflater.push(input, at + length >= entry.compressedSize);
        } catch (error) {
          throw new ZipError('corrupt', `deflate: ${(error as Error).message}`);
        }
        while (pending.length) await emit(pending.shift()!);
        if (length <= 0) break;
      }
      if (!ended) throw new ZipError('corrupt', 'deflate stream did not end');
    }
    if (written !== entry.uncompressedSize) throw new ZipError('corrupt', 'size does not match');
    if (checksum !== entry.crc32) throw new ZipError('checksum', entry.name);
    return await sink.finish();
  } catch (error) {
    sink.abort();
    throw error instanceof ZipError ? error : new ZipError('corrupt', (error as Error).message);
  }
}

/** A text entry such as JSON, read into memory up to the text limit. */
export async function readTextEntry(source: ByteSource, entry: ZipEntry, limits: ZipLimits = DEFAULT_ZIP_LIMITS, options: ExtractOptions = {}): Promise<string> {
  if (entry.uncompressedSize > limits.maxTextBytes) throw new ZipError('too_large', 'text entry');
  return strFromU8(await extractEntry(source, entry, memorySink(), options));
}
